import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { absUrl, geoMeta, keywordsWithLocal } from "@/lib/seo";
import { useState } from "react";
import { ArrowLeft, Check, Plus, ShoppingCart } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { useQuoteCart } from "@/lib/quote-cart";
import { threadProducts, getThreadBySlug, type ThreadProduct } from "@/lib/thread-products";

const SITE_URL = "https://texasmadeshade.com";
const SERVICE_AREA =
  "San Antonio, Seguin, Austin, San Marcos, New Braunfels, Victoria, Corpus Christi, Laredo, Beeville, Junction, Uvalde, Castroville";

export const Route = createFileRoute("/shop/threads/$slug")({
  loader: ({ params }) => {
    const product = getThreadBySlug(params.slug);
    if (!product) throw notFound();
    return { product };
  },
  head: ({ loaderData, params }) => {
    const p = loaderData?.product;
    if (!p) return { meta: [
        ...geoMeta(),{ title: "Thread not found | Texas Made Shade" }] };

    const slug = params?.slug ?? p.slug;
    const canonical = `${SITE_URL}/shop/threads/${slug}`;
    const title = `${p.name} — ${p.size} | Industrial Thread in Texas | Texas Made Shade`;
    const description = `Buy ${p.name} (${p.size}) by ${p.manufacturer}. ${p.blurb} Available in ${p.colors.length}+ colors. Same-week quotes for canvas, awning and marine shops across Central, South and Coastal Texas. Ask about eradicated awnings, backlit awnings and back lite awnings.`;
    const keywords = [
      p.name,
      p.manufacturer,
      ...p.specs.slice(0, 3).map((s: any) => s.value),
      "outdoor thread Texas",
      "bonded polyester thread Texas",
      "marine canvas thread San Antonio",
      "awning thread Seguin",
      "industrial sewing thread Austin",
    ].join(", ");

    // Modeled as a Service (quote-priced supply), not a Product: Google's
    // Product snippet requires offers/review/aggregateRating, and we have no
    // published price for thread.
    const productJsonLd = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: p.name,
      description: p.blurb,
      serviceType: "Industrial Sewing Thread Supply",
      image: p.image.startsWith("http") ? p.image : `${SITE_URL}${p.image}`,
      url: canonical,
      areaServed: SERVICE_AREA,
      provider: {
        "@type": "Organization",
        name: "Texas Made Shade & Covers",
        url: SITE_URL,
      },
    };


    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_URL}/shop` },
        { "@type": "ListItem", position: 3, name: "Industrial Thread", item: `${SITE_URL}/shop#thread` },
        { "@type": "ListItem", position: 4, name: p.name, item: canonical },
      ],
    };

    return {
      meta: [
        ...geoMeta(),
        { title },
        { name: "description", content: description },
        { name: "keywords", content: keywordsWithLocal(keywords) },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: absUrl(p.image) },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: absUrl(p.image) },
        // Texas geo-targeting (per-page)
        { name: "geo.region", content: "US-TX" },
        { name: "geo.placename", content: SERVICE_AREA },
        { name: "geo.position", content: "29.5688;-97.9647" },
        { name: "ICBM", content: "29.5688, -97.9647" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(productJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      ],
    };
  },
  component: ThreadDetailPage,
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-cream">
      <SiteHeader />
      <section className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display text-4xl font-bold uppercase text-navy-deep">Thread not found</h1>
        <p className="mt-3 text-navy-deep/70">That thread isn't in our catalog.</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-xs uppercase tracking-wider text-white hover:bg-red">
          <ArrowLeft className="h-4 w-4" /> Back to Shop
        </Link>
      </section>
      <SiteFooter />
    </main>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <main id="main-content" tabIndex={-1} className="min-h-screen bg-cream">
        <SiteHeader />
        <section className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-bold uppercase text-navy-deep">Something went wrong</h1>
          <p className="mt-3 text-navy-deep/70">{(error as Error)?.message}</p>
          <button
            type="button"
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="mt-6 inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-xs uppercase tracking-wider text-white hover:bg-red"
          >
            Retry
          </button>
        </section>
        <SiteFooter />
      </main>
    );
  },
});

function ThreadDetailPage() {
  const { product } = Route.useLoaderData();
  const { addItem, items, open: openCart } = useQuoteCart();
  const [color, setColor] = useState<string>(product.colors[0] ?? "");
  const [spoolSize, setSpoolSize] = useState<string>(product.sizes[0] ?? "");
  const [justAdded, setJustAdded] = useState(false);

  const inCart = items.some((i) => i.productId === product.id);

  const handleAdd = () => {
    addItem({
      productId: product.id,
      name: product.name,
      size: `${spoolSize} · ${color}`,
      category: `Thread · ${product.manufacturer}`,
      sourceUrl: `/shop/threads/${product.slug}`,
    });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  // Related products: other thread families
  const related: ThreadProduct[] = threadProducts.filter((p) => p.id !== product.id).slice(0, 3);

  return (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-cream">
      <SiteHeader />

      <section className="border-b-2 border-navy-deep/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-6 py-3 font-display text-[11px] uppercase tracking-wider text-navy-deep/70 lg:px-8">
          <Link to="/shop" className="hover:text-red">Shop Marine &amp; Awning Supplies</Link>
          <span>/</span>
          <span className="text-navy-deep/70">Thread</span>
          <span>/</span>
          <span className="text-navy-deep">{product.manufacturer}</span>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-8 lg:py-14">
        <Link
          to="/shop"
          className="mb-6 inline-flex items-center gap-2 font-display text-[11px] uppercase tracking-wider text-navy-deep/70 hover:text-red"
        >
          <ArrowLeft className="h-4 w-4" /> Back to all thread
        </Link>

        <div className="grid gap-10 lg:grid-cols-2">
          {/* Image */}
          <div className="border-2 border-navy-deep/10 bg-white p-6 shadow-card">
            <div className="aspect-square w-full overflow-hidden bg-cream/40">
              <img
                src={product.image}
                alt={`${product.name} — ${product.size}`}
                loading="eager"
                className="h-full w-full object-contain"
              />
            </div>
          </div>

          {/* Info */}
          <div>
            <div className="font-display text-[11px] uppercase tracking-[0.3em] text-red">
              {product.manufacturer}
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold uppercase leading-tight text-navy-deep md:text-4xl">
              {product.name}
            </h1>
            <p className="mt-2 font-display text-lg text-navy-deep/80">{product.size}</p>

            <p className="mt-3 text-xs uppercase tracking-wider text-navy-deep/60">
              Stocked in Seguin, TX · Shipping &amp; pickup for{" "}
              <Link to="/san-antonio" className="text-red underline hover:text-navy-deep">San Antonio</Link>,{" "}
              <Link to="/austin" className="text-red underline hover:text-navy-deep">Austin</Link>,{" "}
              <Link to="/new-braunfels" className="text-red underline hover:text-navy-deep">New Braunfels</Link>,{" "}
              <Link to="/corpus-christi" className="text-red underline hover:text-navy-deep">Corpus Christi</Link>{" "}
              and surrounding Central &amp; South Texas.
            </p>

            <p className="mt-5 text-base leading-relaxed text-navy-deep/80">{product.blurb}</p>

            {product.productNote && (
              <div className="mt-4 border-l-4 border-red bg-cream/60 p-3 text-sm text-navy-deep/80">
                <strong className="font-display uppercase tracking-wider text-[11px] text-red">Note:</strong>{" "}
                {product.productNote}
              </div>
            )}

            {/* Color + size pickers */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="font-display text-[11px] uppercase tracking-wider text-navy-deep">
                  Color
                </label>
                <select
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="mt-1 w-full border-2 border-navy-deep/20 bg-white px-3 py-2 text-sm text-navy-deep focus:border-navy-deep focus:outline-none"
                >
                  {product.colors.map((c: any) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-display text-[11px] uppercase tracking-wider text-navy-deep">
                  Spool / Size
                </label>
                <select
                  value={spoolSize}
                  onChange={(e) => setSpoolSize(e.target.value)}
                  className="mt-1 w-full border-2 border-navy-deep/20 bg-white px-3 py-2 text-sm text-navy-deep focus:border-navy-deep focus:outline-none"
                >
                  {product.sizes.map((s: any) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleAdd}
                className={`inline-flex items-center gap-2 px-6 py-3.5 font-display text-sm uppercase tracking-wider text-white shadow-bold transition ${
                  inCart || justAdded ? "bg-red hover:bg-navy-deep" : "bg-navy-deep hover:bg-red"
                }`}
              >
                {justAdded ? (
                  <><Check className="h-5 w-5" /> Added to Order</>
                ) : inCart ? (
                  <><Plus className="h-5 w-5" /> Add Another</>
                ) : (
                  <><Plus className="h-5 w-5" /> Add to Order</>
                )}
              </button>
              <button
                type="button"
                onClick={openCart}
                className="inline-flex items-center gap-2 border-2 border-navy-deep px-6 py-3.5 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                <ShoppingCart className="h-5 w-5" /> View My Order ({items.length})
              </button>
            </div>

            <p className="mt-3 text-xs text-navy-deep/60">
              No payment now. We'll email a written quote and a QuickBooks invoice you can pay online.
            </p>

            {/* Features */}
            {product.features.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
                  Key Features
                </h3>
                <ul className="mt-3 space-y-2">
                  {product.features.map((f: any) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-navy-deep/80">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-red" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pairs well with — internal cross-links for crawlability */}
            <div className="mt-8 border-t border-navy-deep/10 pt-6">
              <h3 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
                Pairs well with
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm text-navy-deep/80">
                <li>
                  →{" "}
                  <Link to="/shop" hash="hardware" className="text-red underline hover:text-navy-deep">
                    Snaps, grommets &amp; marine hardware
                  </Link>{" "}
                  for finishing seams &amp; corners
                </li>
                <li>
                  →{" "}
                  <Link to="/shop" hash="adhesives" className="text-red underline hover:text-navy-deep">
                    Glues &amp; HH-66 vinyl cement
                  </Link>{" "}
                  for bonding seams &amp; reinforcements
                </li>
                <li>
                  →{" "}
                  <Link to="/shop" hash="boating" className="text-red underline hover:text-navy-deep">
                    Marine canvas, vinyl &amp; YKK® zippers
                  </Link>{" "}
                  to complete a marine canvas build
                </li>
                <li>
                  →{" "}
                  <Link to="/fabrics" className="text-red underline hover:text-navy-deep">
                    Sunbrella® fabric by the yard
                  </Link>{" "}
                  to match thread color to your awning
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Description + specs */}
        <section className="mt-12 grid gap-10 lg:grid-cols-3" aria-labelledby="thread-description-heading">
          <div className="lg:col-span-2">
            <h2 id="thread-description-heading" className="font-display text-2xl font-bold uppercase text-navy-deep">
              About {product.name}
            </h2>
            <div className="mt-4 space-y-4 text-base leading-relaxed text-navy-deep/80">
              {product.description.map((para: any, i: number) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {product.sampleVariants.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
                  Available SKUs (sample)
                </h3>
                <div className="mt-3 overflow-hidden border-2 border-navy-deep/10">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/60 font-display text-[11px] uppercase tracking-wider text-navy-deep">
                      <tr>
                        <th className="px-3 py-2 text-left">Item #</th>
                        <th className="px-3 py-2 text-left">Color</th>
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-left">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-navy-deep/10">
                      {product.sampleVariants.map((v: any) => (
                        <tr key={v.itemNumber}>
                          <td className="px-3 py-2 font-mono text-navy-deep">{v.itemNumber}</td>
                          <td className="px-3 py-2 text-navy-deep/80">{v.color}</td>
                          <td className="px-3 py-2 text-navy-deep/80">{v.size}</td>
                          <td className="px-3 py-2 text-navy-deep/60">{v.note ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <aside className="border-2 border-navy-deep/10 bg-white p-5 shadow-card">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
              Specifications
            </h2>
            <dl className="mt-3 divide-y divide-navy-deep/10 text-sm">
              {product.specs.map((s: any) => (
                <div key={s.label} className="flex justify-between gap-4 py-2">
                  <dt className="text-navy-deep/60">{s.label}</dt>
                  <dd className="text-right font-medium text-navy-deep">{s.value}</dd>
                </div>
              ))}
            </dl>
          </aside>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-16 border-t-2 border-navy-deep/10 pt-10" aria-labelledby="thread-related-heading">
            <h2 id="thread-related-heading" className="font-display text-2xl font-bold uppercase text-navy-deep">
              More industrial thread for awning, marine &amp; canvas work
            </h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to="/shop/threads/$slug"
                  params={{ slug: r.slug }}
                  className="group flex gap-4 border-2 border-navy-deep/10 bg-white p-4 transition hover:border-navy-deep hover:shadow-bold"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden bg-cream/40">
                    <img src={r.image} alt={r.name} loading="lazy" className="h-full w-full object-contain" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[10px] uppercase tracking-widest text-red">
                      {r.manufacturer}
                    </div>
                    <div className="mt-0.5 font-display text-sm font-bold uppercase leading-tight text-navy-deep">
                      {r.name}
                    </div>
                    <div className="mt-0.5 text-xs text-navy-deep/70">{r.size}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </section>

      <LetsTalkForm />
      <SiteFooter />
    </main>
  );
}
